import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Upload, Trash2, Search, Folder, Copy, FileText, Film, Music, Image as ImageIcon } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KIND_ICON = { image: ImageIcon, video: Film, audio: Music, pdf: FileText, doc: FileText, other: FileText };

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => {
    const params = new URLSearchParams();
    if (folder) params.set("folder", folder);
    if (kind) params.set("kind", kind);
    if (q) params.set("q", q);
    api.get(`/admin/media?${params}`).then((r) => setItems(r.data));
    api.get("/admin/media/folders").then((r) => setFolders(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [folder, kind, q]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f);
      try { await api.post(`/admin/media/upload?folder=${encodeURIComponent(folder || "")}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); }
      catch (err) { toast.error(`${f.name}: ${formatApiErrorDetail(err.response?.data?.detail)}`); }
    }
    toast.success(`${files.length} file(s) uploaded`); setBusy(false); if (fileRef.current) fileRef.current.value = ""; load();
  };

  const del = async (mid) => { if (!window.confirm("Delete this asset?")) return; await api.delete(`/admin/media/${mid}`); load(); };
  const copyUrl = (u) => { navigator.clipboard?.writeText(`${BACKEND}${u}`); toast.success("URL copied"); };

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl" data-testid="admin-media-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h1 className="font-serif text-3xl">Media Library</h1><p className="text-paper/50 text-sm mt-1">Central store for images, video, audio and documents. Upload Kajabi assets here.</p></div>
          <label className="inline-flex items-center gap-1.5 bg-heraldic text-white px-3 py-2 rounded-sm text-sm cursor-pointer" data-testid="media-upload-btn"><Upload className="h-4 w-4" /> {busy ? "Uploading…" : "Upload"}<input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="media-file-input" /></label>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-paper/40" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-white/5 border border-white/10 rounded-sm pl-9 pr-3 py-2 text-sm" data-testid="media-search" /></div>
          <input value={folder} onChange={(e) => setFolder(e.target.value)} placeholder="Folder (type to filter / target upload)" className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm w-64" data-testid="media-folder-input" />
          {["", "image", "video", "audio", "pdf", "doc"].map((k) => <button key={k || "all"} onClick={() => setKind(k)} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${kind === k ? "bg-heraldic text-white border-heraldic" : "border-white/15 text-paper/60"}`}>{k || "all"}</button>)}
        </div>
        {folders.length > 0 && <div className="mt-3 flex flex-wrap gap-2 text-xs text-paper/50">{folders.map((f) => <button key={f} onClick={() => setFolder(f)} className="inline-flex items-center gap-1 border border-white/10 px-2 py-1 rounded-sm"><Folder className="h-3 w-3" /> {f}</button>)}</div>}

        <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-grid">
          {items.map((m) => {
            const Ic = KIND_ICON[m.kind] || FileText;
            return (
              <div key={m.mid} className="border border-white/10 rounded-sm overflow-hidden bg-white/5 group" data-testid={`media-item-${m.mid}`}>
                <div className="aspect-square bg-black/30 flex items-center justify-center overflow-hidden">
                  {m.kind === "image" ? <img src={`${BACKEND}${m.url}`} alt={m.name} className="w-full h-full object-cover" /> : <Ic className="h-8 w-8 text-paper/40" />}
                </div>
                <div className="p-2">
                  <div className="text-xs truncate" title={m.name}>{m.name}</div>
                  <div className="text-[10px] text-paper/40">{m.kind}{m.folder && ` · ${m.folder}`}</div>
                  <div className="flex gap-1 mt-1.5"><button onClick={() => copyUrl(m.url)} className="text-[10px] border border-white/15 px-1.5 py-1 rounded-sm inline-flex items-center gap-1"><Copy className="h-3 w-3" /> URL</button><button onClick={() => del(m.mid)} className="text-[10px] border border-white/15 px-1.5 py-1 rounded-sm text-red-400"><Trash2 className="h-3 w-3" /></button></div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="col-span-full text-center text-paper/40 py-12 text-sm">No assets yet. Upload your Kajabi files here.</div>}
        </div>
      </div>
    </AdminLayout>
  );
}
